import React from 'react';
import PropTypes from 'prop-types';
import { css, cx } from 'emotion';
import Flexbox from 'flexbox-react';

import { Collapse } from 'antd';

import { colorTextBlack, colorVeryLightGrey } from 'styles/base.js';

const Panel = Collapse.Panel;

const resultItemContainer = css`
    padding: 8px 8px 10px 8px;
    font-size: 12px;
    width: 100%;
`;
const title = css`
    font-weight: bold;
    font-size: 14px;
    a:hover {
        text-decoration: underline !important;
    }
`;
const hitInfoContainer = css`
    overflow-x: hidden;
`;
const hitInfoLabel = css`
    font-style: italic;
    margin-right: 3px;
    color: #adadad;
`;
const hitInfoValue = css`
    cursor: pointer;
    font-weight: normal;
    color: ${colorTextBlack} !important;
    overflow: hidden;
    text-overflow: ellipsis;
    max-height: 18px;
    &:hover {
        text-decoration: underline !important;
    }
    &.bold {
        font-weight: 800;
    }
`;
const snippetsContainer = css`
    display: inline-flex;
    width: 100%;
    border-bottom: 1px solid ${colorVeryLightGrey};
    margin-bottom: 10px;
`;
const snippetHeader = css`
    width: 100%;
    height: 8px;
    background: ${colorVeryLightGrey};
    border: 1px solid #eaeaea;
`;
const lineNumberContainer = css`
    min-width: 65px;
    padding: 10px 10px 10px 0;
    background: ${colorVeryLightGrey};
    border-width: 0 1px 1px 1px;
    border-color: #eaeaea;
    text-align: right;
    font-size: 14px;
    color: #bbbbbb;
    div {
        height: 20px;
    }
`;
const lineCodeContainer = css`
    padding: 10px;
    line-height: 20px;
    font-size: 14px;
    white-space: pre;
    overflow-x: auto;
    div {
        height: 20px;
        em {
            color: #464646;
            background: transparent;
            font-weight: 700;
            font-style: normal;
        }
    }
`;
const hitContainer = css`
    width: 100%;
    background-color: transparent;
    border: 0;
    /* margin-left: 30px; */
    .ant-collapse-item {
        border: 0;
        .ant-collapse-header {
            padding: 0 !important;
            line-height: unset;
            .ant-collapse-arrow, .arrow {
                position: absolute;
                top: 10px;
                right: 0;
                left: unset;
                transform: scaleX(-1);
                text-align: unset;
            }
        }
        .ant-collapse-content {
            border-top: 0;
            white-space: pre;
            font-family: monospace;
            color: #989898;
            .ant-collapse-content-box {
                padding: 0 0 0 16px;
            }
        }
    }
`;

export default class HitResult extends React.Component {
    static propTypes = {
        result: PropTypes.object,
        sortingStyle: PropTypes.object,
        isExpandAll: PropTypes.bool,
    };

    constructor(props) {
        super(props);
        this.state = {
            isExpanded: props.isExpandAll
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.isExpandAll !== this.props.isExpandAll) {
            if (this.state.isExpanded !== this.props.isExpandAll) {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ isExpanded: this.props.isExpandAll });
            }
        }
    }

    expandOnChange = () => {
        this.setState({ isExpanded: !(this.state.isExpanded) });
    }

    ignoreExpandOnClick = (event) => {
        event.stopPropagation();
    }

    render() {
        const { result, sortingStyle } = this.props;
        const { isExpanded } = this.state;
        const { _source, highlight } = result;
        // Setup urls
        const codeArmGov = 'https://code.arm.gov/';
        const groupUrl = `${codeArmGov}${_source.group_name}`;
        const projectUrl = `${groupUrl}/${_source.project_name}`;
        const fileUrl = `${projectUrl}/blob/master/${_source.file_path}`;

        let document = (
            <Flexbox className={cx(resultItemContainer, 'result-item')} flexDirection="column">
                <div className={title}>
                    <a onClick={this.ignoreExpandOnClick}
                        href={fileUrl}
                        target="_blank"
                    >
                        {_source.file_name}
                        <i className={cx('fa fa-external-link', css`margin-left: 5px;`)} aria-hidden="true"/>
                    </a>
                </div>
                <Flexbox flexDirection="row" alignItems="center">
                    <Flexbox className={hitInfoContainer} flexDirection="row" minWidth="130px">
                        <div className={hitInfoLabel}>group:</div>
                        <a className={cx(hitInfoValue, sortingStyle.groupStyle)}
                            onClick={this.ignoreExpandOnClick}
                            href={groupUrl}
                            target="_blank"
                            title={_source.group_name}
                        >
                            {_source.group_name}
                        </a>
                    </Flexbox>
                    <Flexbox className={hitInfoContainer} flexDirection="row" minWidth="180px">
                        <div className={hitInfoLabel}>project:</div>
                        <a className={cx(hitInfoValue, sortingStyle.projectStyle)}
                            onClick={this.ignoreExpandOnClick}
                            href={projectUrl}
                            target="_blank"
                            title={_source.project_name}
                        >
                            {_source.project_name}
                        </a>
                    </Flexbox>
                    <Flexbox className={hitInfoContainer} flexDirection="row" flex="1">
                        <div className={hitInfoLabel}>path:</div>
                        <a className={cx(hitInfoValue, sortingStyle.pathStyle)}
                            onClick={this.ignoreExpandOnClick}
                            href={fileUrl}
                            target="_blank"
                            title={`/${_source.file_path}`}
                        >
                            {_source.file_path}
                        </a>
                    </Flexbox>
                </Flexbox>
            </Flexbox>
        );
        if (highlight) {
            const numberLines = [];
            const codeLines = [];
            const snippets = highlight['attachment.content'];
            for (let i = 0; i < snippets.length; i += 1) {
                const snippet = snippets[i];
                const indexAfterNumber = snippet.indexOf(' ');
                const number = snippet.slice(0, indexAfterNumber);
                if (isNaN(number)) {
                    numberLines.push(<div key={i}/>);
                    codeLines.push(<div key={i} dangerouslySetInnerHTML={{ __html: snippet }}/>);
                } else {
                    numberLines.push(<div key={i}>{number}</div>);
                    codeLines.push(<div key={i} dangerouslySetInnerHTML={{ __html: snippet.slice(indexAfterNumber) }}/>);
                }
            }
            document = (
                <Collapse className={hitContainer} onChange={this.expandOnChange} activeKey={(isExpanded) ? '1' : ''}>
                    <Panel header={document} key="1">
                        <div className={snippetHeader}/>
                        <div className={cx('snippetsContainer', snippetsContainer)}>
                            <div className={lineNumberContainer}>
                                {numberLines}
                            </div>
                            <div className={lineCodeContainer}>
                                {codeLines}
                            </div>
                        </div>
                    </Panel>
                </Collapse>
            );
        }
        return (document);
    }
}



// WEBPACK FOOTER //
// ./src/components/page/search/components/HitResult.js